const componentGroups = [
  {
    selector: 'quick-add-component, quick-add-dialog',
    modules: [
      '@theme/quick-add',
      '@theme/product-form',
      '@theme/fly-to-cart',
      '@theme/component-quantity-selector',
    ],
  },
  {
    selector: 'product-form-component, add-to-cart-component, fly-to-cart',
    modules: ['@theme/product-form', '@theme/fly-to-cart'],
  },
  { selector: 'product-card, product-card-link, swatches-variant-picker-component', modules: ['@theme/product-card'] },
  { selector: 'variant-picker', modules: ['@theme/variant-picker'] },
  { selector: 'accordion-custom', modules: ['@theme/accordion-custom'] },
  { selector: 'deferred-media, product-model', modules: ['@theme/media'] },
  { selector: 'product-price', modules: ['@theme/product-price'] },
  { selector: 'product-sku-component', modules: ['@theme/product-sku'] },
  { selector: 'product-inventory', modules: ['@theme/product-inventory'] },
  { selector: 'show-more-component', modules: ['@theme/show-more'] },
  { selector: 'slideshow-component', modules: ['@theme/slideshow'] },
  { selector: 'layered-slideshow-component', modules: ['@theme/layered-slideshow'] },
  { selector: 'anchored-popover-component', modules: ['@theme/anchored-popover'] },
  { selector: 'floating-panel-component', modules: ['@theme/floating-panel'] },
  { selector: 'video-background-component', modules: ['@theme/video-background'] },
  { selector: 'quantity-selector-component', modules: ['@theme/component-quantity-selector'] },
  { selector: 'media-gallery', modules: ['@theme/media-gallery'] },
  { selector: 'rte-formatter', modules: ['@theme/rte-formatter'] },
  { selector: 'volume-pricing', modules: ['@theme/volume-pricing'] },
  { selector: 'price-per-item', modules: ['@theme/price-per-item'] },
  { selector: 'volume-pricing-info', modules: ['@theme/volume-pricing-info'] },
];

/** @type {Map<string, Promise<unknown>>} */
const moduleRequests = new Map();
const observedElements = new WeakSet();

/**
 * Loads a module once and keeps failed requests retryable.
 * @param {string} moduleName
 */
function loadModule(moduleName) {
  const pendingRequest = moduleRequests.get(moduleName);
  if (pendingRequest) return pendingRequest;

  const request = import(moduleName).catch((error) => {
    moduleRequests.delete(moduleName);
    console.error(`[AWK] Komponente konnte nicht geladen werden: ${moduleName}`, error);
    throw error;
  });

  moduleRequests.set(moduleName, request);
  return request;
}

/** @param {{ modules: string[] }} group */
function loadGroup(group) {
  return Promise.all(group.modules.map(loadModule));
}

const intersectionObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const group = componentGroups.find(({ selector }) => entry.target.matches(selector));
            if (group) loadGroup(group);
            intersectionObserver?.unobserve(entry.target);
          }
        },
        { rootMargin: '900px 0px' }
      )
    : null;

/**
 * Registers matching elements for near-viewport loading.
 * @param {Document | DocumentFragment | Element} root
 */
function scan(root) {
  for (const group of componentGroups) {
    /** @type {Element[]} */
    const elements = [];

    if (root instanceof Element && root.matches(group.selector)) elements.push(root);
    elements.push(...root.querySelectorAll(group.selector));

    for (const element of elements) {
      if (observedElements.has(element)) continue;
      observedElements.add(element);

      if (intersectionObserver) {
        intersectionObserver.observe(element);
      } else {
        loadGroup(group);
      }
    }
  }
}

for (const eventName of ['pointerover', 'pointerdown', 'focusin']) {
  document.addEventListener(
    eventName,
    (event) => {
      if (!(event.target instanceof Element)) return;

      const group = componentGroups.find(({ selector }) => event.target.closest(selector));
      if (group) loadGroup(group);
    },
    { capture: true, passive: true }
  );
}

if (!('showPopover' in HTMLElement.prototype)) {
  loadModule('@theme/popover-polyfill');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
} else {
  scan(document);
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof Element || node instanceof DocumentFragment) scan(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
